import { useEffect, useRef, useState, useCallback } from "react";

interface StatCardProps {
  value: string;
  label: string;
  icon: string;
  index: number;
  style?: React.CSSProperties;
}

function StatCard({ value, label, icon, index, style }: StatCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glowPos, setGlowPos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setTilt({
      x: ((y - cy) / cy) * -10,
      y: ((x - cx) / cx) * 10,
    });
    setGlowPos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  }, []);

  const baseRotations = [-4, 3, -2, 5];
  const baseRotation = baseRotations[index % baseRotations.length];

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={() => setIsHovered(true)}
      className="absolute cursor-pointer select-none"
      style={{
        ...style,
        transform: isHovered
          ? `rotate(0deg) perspective(600px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(1.06) translateZ(10px)`
          : `rotate(${baseRotation}deg) perspective(600px) rotateX(0deg) rotateY(0deg) scale(1)`,
        transition: isHovered
          ? "transform 0.08s ease-out"
          : "transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
        transformStyle: "preserve-3d",
        zIndex: isHovered ? 20 : undefined,
      }}
    >
      <div
        className="relative overflow-hidden rounded-xl border border-white/15 backdrop-blur-md px-5 py-5 w-[140px] md:w-[160px]"
        style={{
          background: isHovered
            ? `radial-gradient(circle at ${glowPos.x}% ${glowPos.y}%, rgba(166,106,63,0.25) 0%, rgba(15,15,15,0.88) 65%)`
            : "rgba(15,15,15,0.75)",
          boxShadow: isHovered
            ? "0 16px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 4px 24px rgba(0,0,0,0.4)",
          transition: "background 0.15s ease, box-shadow 0.3s ease",
        }}
      >
        {/* Top accent */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background: isHovered
              ? "linear-gradient(90deg, transparent, #A66A3F, transparent)"
              : "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
            transition: "background 0.3s ease",
          }}
        />

        {/* Icon */}
        <div className="w-8 h-8 flex items-center justify-center mb-3">
          <i
            className={`${icon} text-xl`}
            style={{
              color: isHovered ? "#A66A3F" : "rgba(255,255,255,0.4)",
              transition: "color 0.3s ease",
            }}
          />
        </div>

        {/* Value */}
        <div
          className="text-2xl md:text-3xl font-bold tracking-tight leading-none mb-1.5"
          style={{
            color: isHovered ? "#A66A3F" : "#ffffff",
            transition: "color 0.3s ease",
          }}
        >
          {value}
        </div>

        {/* Label */}
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-white/35 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const contentRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const content = contentRef.current;
    const cards = cardsRef.current;
    if (!content || !cards) return;

    content.style.opacity = "0";
    content.style.transform = "translateY(32px)";
    cards.style.opacity = "0";
    cards.style.transform = "translateY(20px)";

    const t1 = setTimeout(() => {
      content.style.transition = "opacity 1s ease, transform 1s ease";
      content.style.opacity = "1";
      content.style.transform = "translateY(0)";
    }, 300);

    const t2 = setTimeout(() => {
      cards.style.transition = "opacity 0.9s ease, transform 0.9s ease";
      cards.style.opacity = "1";
      cards.style.transform = "translateY(0)";
    }, 700);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const stats = [
    {
      value: "100 MWh",
      label: "Kapasite",
      icon: "ri-battery-2-charge-line",
      pos: { top: "8%", right: "2%" },
    },
    {
      value: "%92+",
      label: "RTE Verim",
      icon: "ri-flashlight-line",
      pos: { top: "32%", right: "18%" },
    },
    {
      value: "IP55",
      label: "Koruma",
      icon: "ri-shield-check-line",
      pos: { top: "56%", right: "4%" },
    },
    {
      value: "20+ Yıl",
      label: "Ömür",
      icon: "ri-time-line",
      pos: { top: "76%", right: "22%" },
    },
  ];

  return (
    <section className="relative w-full overflow-hidden" style={{ minHeight: "100svh" }}>
      {/* Background Image */}
      <img
        src="https://static.readdy.ai/image/ecb21a4bf5131aa1974d0073377f0964/a89f374a69ac34772ee70a19f792d9d5.png"
        alt="AKSESS Prefabrike Enerji Depolama Sistemi"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/55 to-[#1a1a1a]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/70 via-transparent to-[#1a1a1a]/30" />

      {/* Main Content */}
      <div className="relative z-10 flex items-center min-h-[100svh] pt-20 pb-10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 w-full">

          {/* Desktop: side by side | Mobile: stacked */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 lg:gap-0">

            {/* LEFT — Text content */}
            <div ref={contentRef} className="max-w-[520px] lg:max-w-[500px]">

              {/* Eyebrow */}
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-6 md:w-8 h-px bg-[#A66A3F] flex-shrink-0" />
                <span className="text-[#A66A3F] text-[10px] md:text-[11px] font-semibold tracking-[0.2em] uppercase leading-none">
                  Battery Energy Storage System
                </span>
              </div>

              {/* Heading */}
              <h1 className="font-bold leading-[1.1] tracking-tight mb-5 md:mb-6">
                <span className="block text-[32px] md:text-5xl lg:text-[56px] text-white">
                  Prefabrike Enerji
                </span>
                <span className="block text-[32px] md:text-5xl lg:text-[56px] text-[#A66A3F]">
                  Depolama
                </span>
                <span className="block text-[32px] md:text-5xl lg:text-[56px] text-white">
                  Çözümleri
                </span>
              </h1>

              {/* Divider */}
              <div className="w-12 h-px bg-white/20 mb-5 md:mb-6" />

              {/* Subtitle */}
              <p className="text-white/60 text-[14px] md:text-[16px] font-light leading-[1.8] mb-8 md:mb-10 max-w-[400px]">
                Modüler, ölçeklenebilir ve yüksek verimli enerji depolama sistemleri.
                Sahada hızlı kurulum, uzun ömürlü altyapı.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => scrollTo("urun-nedir")}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#A66A3F] text-white text-[13px] font-semibold tracking-[0.06em] hover:bg-[#8f5a33] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Detayları İncele
                  <i className="ri-arrow-right-line text-sm" />
                </button>
                <button
                  onClick={() => scrollTo("iletisim")}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-white/30 text-white/75 text-[13px] font-medium tracking-[0.06em] hover:border-white/60 hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  İletişime Geç
                </button>
              </div>

            </div>

            {/* RIGHT — Scattered stat cards (desktop) */}
            <div
              ref={cardsRef}
              className="hidden lg:block relative flex-shrink-0"
              style={{ width: "420px", height: "480px" }}
            >
              {stats.map((stat, i) => (
                <StatCard
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                  icon={stat.icon}
                  index={i}
                  style={stat.pos}
                />
              ))}
            </div>

            {/* Mobile — 2x2 grid cards */}
            <div className="grid grid-cols-2 gap-3 lg:hidden">
              {stats.map((stat, i) => (
                <MobileStatCard
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                  icon={stat.icon}
                  index={i}
                />
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#1a1a1a]/60 to-transparent z-10 pointer-events-none" />
    </section>
  );
}

function MobileStatCard({
  value,
  label,
  icon,
  index,
}: {
  value: string;
  label: string;
  icon: string;
  index: number;
}) {
  const [isPressed, setIsPressed] = useState(false);

  return (
    <div
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      className="relative overflow-hidden rounded-xl border border-white/12 backdrop-blur-md px-4 py-4"
      style={{
        background: isPressed ? "rgba(166,106,63,0.18)" : "rgba(15,15,15,0.72)",
        transform: isPressed ? "scale(0.97)" : "scale(1)",
        transition: "transform 0.15s ease, background 0.15s ease",
        animationDelay: `${0.7 + index * 0.1}s`,
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="w-7 h-7 flex items-center justify-center mb-2">
        <i className={`${icon} text-lg text-[#A66A3F]`} />
      </div>
      <div className="text-xl font-bold tracking-tight text-white leading-none mb-1">
        {value}
      </div>
      <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-white/35">
        {label}
      </div>
    </div>
  );
}
